import {MaterialNode} from "#material/nodes/base/material/material-node"
import type {ColorRampSockets} from "#material/nodes/color/color-ramp/definition"
import {colorRampDefinition} from "#material/nodes/color/color-ramp/definition"
import type {CyclesReturns} from "#material/sockets/cycles"
import {ThreeApplyLUTNode, createLutTypedArray, lutSize, threeConvert, threeValue} from "#material/three/three-utils"
import type {CyclesEvaluator, ThreeEvaluator} from "#material/nodes/base/material/types/evaluators"
import type {Color} from "@cm/graph-v3"
import {registerMaterialGraphNode} from "#material/register"
import {Three as THREE, ThreeTsl as TSL} from "#material/three/exports"
import {cyclesColor} from "#material/cycles/helpers"
import type {ThreeReturns} from "#material/sockets/three"
import type {RgbRampNode} from "#material/cycles/types"
import type {ColorStop, InterpolationType} from "#material/nodes/color/color-ramp/types"
import type {MaterialNodeConstructorParameters, MaterialNodeParameters, MaterialNodeSettings} from "#material/sockets/parameters"
import {stops, numberOfStops} from "#material/nodes/color/color-ramp/helpers"
import {hslToRgb, hsvToRgb, interpolateHue, rgbToHsl, rgbToHsv} from "#material/nodes/color/color-ramp/color-space"

@registerMaterialGraphNode
export class ColorRamp extends MaterialNode<ColorRampSockets> {
    public override get definition() {
        return colorRampDefinition
    }

    public constructor(
        options:
            | {parameters: MaterialNodeConstructorParameters<ColorRampSockets>; disableValidation?: false}
            | {parameters: MaterialNodeParameters<ColorRampSockets>; disableValidation: true},
    ) {
        super(options)

        if (numberOfStops(options.parameters.settings) < 2) {
            this.addStop({r: 0, g: 0, b: 0}, 1)
            this.addStop({r: 1, g: 1, b: 1}, 1)
        }
    }

    override toThree = async (evaluator: ThreeEvaluator): Promise<ThreeReturns<ColorRampSockets>> => {
        const {context} = evaluator
        const {logger} = context
        const parameters = this.parameters.settings
        const inputs = this.parameters.inputs

        const {colorRampCyclesRampColorTable, colorRampCyclesRampAlphaTable} = ColorRamp.lookupTableFromStops(this.parameters.settings)
        const fac = (await evaluator.get(inputs.fac))?.toFloat() ?? threeConvert(parameters.fac, threeValue) ?? threeValue(1)

        if (
            colorRampCyclesRampColorTable &&
            colorRampCyclesRampAlphaTable &&
            colorRampCyclesRampColorTable.length === colorRampCyclesRampAlphaTable.length &&
            colorRampCyclesRampColorTable.length === lutSize
        ) {
            const lutRgba = colorRampCyclesRampColorTable.map((color, index) => [color.r, color.g, color.b, colorRampCyclesRampAlphaTable.at(index)!])

            const getTexture = () => {
                const {threePushLutTexture} = context
                if (!threePushLutTexture) {
                    logger.warn("ColorRamp: No threePushLutTexture function provided, using local DataTexture instead")

                    const floatType = context.threeDefaultFloatTextureType ?? THREE.HalfFloatType
                    const texture = new THREE.DataTexture(createLutTypedArray(lutRgba.flat(), floatType), lutRgba.length, 1, THREE.RGBAFormat, floatType)
                    texture.minFilter = THREE.NearestFilter
                    texture.magFilter = THREE.NearestFilter
                    texture.wrapS = THREE.ClampToEdgeWrapping
                    texture.wrapT = THREE.ClampToEdgeWrapping
                    texture.anisotropy = 1
                    texture.colorSpace = THREE.LinearSRGBColorSpace
                    texture.needsUpdate = true
                    context.onThreeCreatedTexture?.(texture)
                    return {texture, index: 0}
                } else {
                    return threePushLutTexture(lutRgba)
                }
            }

            const {texture, index} = getTexture()

            const result = new ThreeApplyLUTNode(TSL.vec4(fac), texture, index, threeValue(1))

            return {color: result.rgb.toColor(), alpha: result.a}
        } else throw new Error("ColorRamp table needs to be provided with exactly 256 entries")
    }

    override toCycles = async (evaluator: CyclesEvaluator): Promise<CyclesReturns<ColorRampSockets>> => {
        const {colorRampCyclesRampColorTable, colorRampCyclesRampAlphaTable} = ColorRamp.lookupTableFromStops(this.parameters.settings)
        if (
            !colorRampCyclesRampColorTable ||
            !colorRampCyclesRampAlphaTable ||
            colorRampCyclesRampColorTable.length !== colorRampCyclesRampAlphaTable.length ||
            colorRampCyclesRampColorTable.length !== lutSize
        )
            throw new Error("ColorRamp table needs to be provided with exactly 256 entries")

        const rgbRampNode: RgbRampNode = {
            type: "rgb_ramp",
            inputs: {fac: await evaluator.get(this.parameters.inputs.fac)},
            parameters: {
                fac: this.parameters.settings.fac,
                ramp: colorRampCyclesRampColorTable.map((color) => cyclesColor(color)!),
                ramp_alpha: colorRampCyclesRampAlphaTable.map((alpha) => alpha ?? 1),
                interpolate: undefined,
            },
            resources: {},
        }

        return {alpha: [rgbRampNode, "alpha"], color: [rgbRampNode, "color"]}
    }

    public addStop = (color = {r: 1, g: 1, b: 1}, alpha = 1) => {
        // const allStops = stops(this.parameters.settings)
        const total = numberOfStops(this.parameters.settings)
        const position = total === 0 ? 0 : 1 // Math.min(1, Math.max(0, total > 0 ? (allStops[total - 1].position + (total > 1 ? allStops[total - 2].position : 0)) / 2 : 0.5))

        this.updateSetting(`colorRampElements_${total}Color_0`, color.r)
        this.updateSetting(`colorRampElements_${total}Color_1`, color.g)
        this.updateSetting(`colorRampElements_${total}Color_2`, color.b)
        this.updateSetting(`colorRampElements_${total}Alpha`, alpha)
        this.updateSetting(`colorRampElements_${total}Position`, position)
    }

    public removeStop = (index: number) => {
        const total = numberOfStops(this.parameters.settings)
        if (total <= 2) {
            // Keep at least 2 stops
            return
        }

        // shift subsequent entries by one
        for (let i = index + 1; i < total; i++) {
            this.updateSetting(`colorRampElements_${i - 1}Color_0`, this.parameters.settings[`colorRampElements_${i}Color_0`])
            this.updateSetting(`colorRampElements_${i - 1}Color_1`, this.parameters.settings[`colorRampElements_${i}Color_1`])
            this.updateSetting(`colorRampElements_${i - 1}Color_2`, this.parameters.settings[`colorRampElements_${i}Color_2`])
            this.updateSetting(`colorRampElements_${i - 1}Alpha`, this.parameters.settings[`colorRampElements_${i}Alpha`])
            this.updateSetting(`colorRampElements_${i - 1}Position`, this.parameters.settings[`colorRampElements_${i}Position`])
        }

        // remove the last element
        delete this.parameters.settings[`colorRampElements_${total - 1}Color_0`]
        delete this.parameters.settings[`colorRampElements_${total - 1}Color_1`]
        delete this.parameters.settings[`colorRampElements_${total - 1}Color_2`]
        delete this.parameters.settings[`colorRampElements_${total - 1}Alpha`]
        delete this.parameters.settings[`colorRampElements_${total - 1}Position`]
    }

    public updateStop = (index: number, partialStop: Partial<ColorStop>, generateLut = true) => {
        if (index < 0 || index > numberOfStops(this.parameters.settings)) {
            throw new Error(`Cannot update stop at index ${index}, there are only ${numberOfStops(this.parameters.settings)} stops.`)
        }

        const stop = {...stops(this.parameters.settings)[index], ...partialStop}
        this.updateSetting(`colorRampElements_${index}Color_0`, stop.color.r)
        this.updateSetting(`colorRampElements_${index}Color_1`, stop.color.g)
        this.updateSetting(`colorRampElements_${index}Color_2`, stop.color.b)
        this.updateSetting(`colorRampElements_${index}Alpha`, stop.alpha)
        this.updateSetting(`colorRampElements_${index}Position`, Math.min(1, Math.max(0, stop.position)))
    }

    public get stops(): ColorStop[] {
        return stops(this.parameters.settings)
    }

    public static lookupTableFromStops = (settings: MaterialNodeSettings<ColorRampSockets>) => {
        const colorTable: {r: number; g: number; b: number}[] = []
        const alphaTable: number[] = []

        const colorMode = settings.colorRampColorMode ?? "RGB"
        const hueInterpolation = settings.colorRampHueInterpolation ?? "NEAR"
        // Blender ignores the interpolation outside RGB and always blends the two neighbouring stops
        const interpolation = colorMode === "RGB" ? (settings.colorRampInterpolation ?? "LINEAR") : "LINEAR"

        // Helper functions for weights
        function keyCurvePositionWeights(t: number, type: InterpolationType): number[] {
            if (type === "CONSTANT") {
                return [0, 0, 1, 0]
            } else if (type === "LINEAR") {
                return [0, -t + 1, t, 0]
            } else if (type === "EASE") {
                const t2 = t * t
                return keyCurvePositionWeights(3 * t2 - 2 * t2 * t, "LINEAR")
            } else if (type === "CARDINAL") {
                const t2 = t * t
                const t3 = t2 * t
                const fc = 0.71
                return [-fc * t3 + 2 * fc * t2 - fc * t, (2 - fc) * t3 + (fc - 3) * t2 + 1, (fc - 2) * t3 + (3 - 2 * fc) * t2 + fc * t, fc * t3 - fc * t2]
            } else if (type === "B_SPLINE") {
                const t2 = t * t
                const t3 = t2 * t
                return [
                    -0.16666666 * t3 + 0.5 * t2 - 0.5 * t + 0.16666666,
                    0.5 * t3 - t2 + 0.66666666,
                    -0.5 * t3 + 0.5 * t2 + 0.5 * t + 0.16666666,
                    0.16666666 * t3,
                ]
            }

            throw new Error(`Unknown interpolation type: ${type}`)
        }

        // Sort stops by position
        const sortedStops = [...stops(settings)].sort((a, b) => a.position - b.position)
        const tot = sortedStops.length

        for (let i = 0; i < 256; i++) {
            const t = Math.fround(i / 255)
            let color: Color = {r: 0, g: 0, b: 0}
            let alpha = 0

            if (tot === 0) {
                color = {r: 0, g: 0, b: 0}
                alpha = 1
            } else if (tot === 1 || (t <= sortedStops[0].position && ["LINEAR", "EASE", "CONSTANT"].includes(interpolation))) {
                color = sortedStops[0].color
                alpha = sortedStops[0].alpha
            } else {
                let a = 0
                for (; a < tot; a++) {
                    if (sortedStops[a].position > t) break
                }

                // Blender holds the left stop's colour through a constant segment, and past the last stop
                if (interpolation === "CONSTANT" || (a === tot && ["LINEAR", "EASE"].includes(interpolation))) {
                    const leftStop = sortedStops[Math.max(0, Math.min(tot - 1, a - 1))]
                    colorTable.push(leftStop.color)
                    alphaTable.push(leftStop.alpha)
                    continue
                }

                let leftStopPosition = 0
                let rightStopPosition = 1
                if (a === tot) {
                    leftStopPosition = sortedStops[tot - 1].position
                } else if (a === 0) {
                    rightStopPosition = sortedStops[0].position
                } else {
                    leftStopPosition = sortedStops[a - 1].position
                    rightStopPosition = sortedStops[a].position
                }

                // Our fac runs from the left stop, Blender's from the right, so the degenerate case mirrors it
                let fac = rightStopPosition !== leftStopPosition ? (t - leftStopPosition) / (rightStopPosition - leftStopPosition) : a !== tot ? 1 : 0
                fac = Math.max(0, Math.min(1, fac))

                // Blender order: cbd0 (right+1), cbd1 (right), cbd2 (left), cbd3 (left-1)
                // weights: t[0]*cbd0 + t[1]*cbd1 + t[2]*cbd2 + t[3]*cbd3
                let cbd0: ColorStop, cbd1: ColorStop, cbd2: ColorStop, cbd3: ColorStop
                // Clamp indices to valid range
                const idx0 = Math.max(0, Math.min(tot - 1, a - 2))
                const idx1 = Math.max(0, Math.min(tot - 1, a - 1))
                const idx2 = Math.max(0, Math.min(tot - 1, a))
                const idx3 = Math.max(0, Math.min(tot - 1, a + 1))
                cbd0 = sortedStops[idx0]
                cbd1 = sortedStops[idx1]
                cbd2 = sortedStops[idx2]
                cbd3 = sortedStops[idx3]

                if (colorMode !== "RGB") {
                    const right = cbd2
                    const left = cbd1
                    const toSpace = colorMode === "HSV" ? rgbToHsv : rgbToHsl
                    const fromSpace = colorMode === "HSV" ? hsvToRgb : hslToRgb
                    const [hueRight, secondRight, thirdRight] = toSpace(right.color)
                    const [hueLeft, secondLeft, thirdLeft] = toSpace(left.color)
                    color = fromSpace([
                        interpolateHue(hueInterpolation, fac, 1 - fac, hueRight, hueLeft),
                        fac * secondRight + (1 - fac) * secondLeft,
                        fac * thirdRight + (1 - fac) * thirdLeft,
                    ])
                    alpha = fac * right.alpha + (1 - fac) * left.alpha
                    colorTable.push(color)
                    alphaTable.push(alpha)
                    continue
                }

                const weights = keyCurvePositionWeights(fac, interpolation)
                color = {
                    r: weights[0] * cbd0.color.r + weights[1] * cbd1.color.r + weights[2] * cbd2.color.r + weights[3] * cbd3.color.r,
                    g: weights[0] * cbd0.color.g + weights[1] * cbd1.color.g + weights[2] * cbd2.color.g + weights[3] * cbd3.color.g,
                    b: weights[0] * cbd0.color.b + weights[1] * cbd1.color.b + weights[2] * cbd2.color.b + weights[3] * cbd3.color.b,
                }
                alpha = weights[0] * cbd0.alpha + weights[1] * cbd1.alpha + weights[2] * cbd2.alpha + weights[3] * cbd3.alpha
                // Clamp
                color.r = Math.max(0, Math.min(1, color.r))
                color.g = Math.max(0, Math.min(1, color.g))
                color.b = Math.max(0, Math.min(1, color.b))
                alpha = Math.max(0, Math.min(1, alpha))
            }
            colorTable.push(color)
            alphaTable.push(alpha)
        }
        return {colorRampCyclesRampColorTable: colorTable, colorRampCyclesRampAlphaTable: alphaTable}
    }
}
